/**
 * SUIVI RH DES CONTRATS — fonctions PURES de l'écran (lot 1, retours de Mathilde du
 * 26/10/2026, RH › Contrats › Pilotage › Suivi des contrats).
 *
 * Ce fichier ne calcule AUCUNE règle de période d'essai : les dates (fin PE 1 à −1 jour,
 * fin PE 2, report, prévenance, date de fin de rupture plafonnée) viennent du serveur
 * (contrat d'interface du lot 1, module utils/periode_essai.py). L'écran ne fait que
 * placer, filtrer et dire. Une seule exception assumée : `dateApresPrevenance` sert de
 * repli quand la date de remise change dans la fenêtre courriel avant la réponse du
 * serveur ; le serveur revalide de toute façon (422 rupture_sous_minimum_legal).
 */
import { sansAccents } from './ficheCollab';

// ── Statuts RH : 7 clés fixes (R-10), couleurs par variables CSS (index.css) ──────
export const STATUTS_RH = [
  { cle: 'sorti', libelle: 'Sorti', variable: '--rh-sorti' },
  { cle: 'en_cours_de_sortie', libelle: 'En cours de sortie', variable: '--rh-en-cours-de-sortie' },
  { cle: 'a_verifier', libelle: 'À vérifier', variable: '--rh-a-verifier' },
  { cle: 'arret_pe_ou_renouvellement', libelle: 'Arrêt PE ou renouvellement à faire', variable: '--rh-arret-pe' },
  { cle: 'urgent', libelle: 'Urgent', variable: '--rh-urgent' },
  { cle: 'annule', libelle: 'Annulé', variable: '--rh-annule' },
  { cle: 'fait_en_paie', libelle: 'Fait en paie', variable: '--rh-fait-en-paie' },
];
export const STATUT_AUCUN = { cle: '', libelle: '—', variable: '--rh-aucun' };
export const CLES_STATUTS_RH = STATUTS_RH.map((s) => s.cle);

// Lot 5 (27/10/2026) : la pastille « Hors liste » (valeur tapée hors de la liste, gardée telle quelle).
export const HORS_LISTE = '__hors_liste';

const variableAjout = (cle) => `--rh-statut-${String(cle || '').toLowerCase().replace(/[^a-z0-9_-]/g, '')}`;

/**
 * La configuration d'un statut RH ; une clé inconnue ou vide rend « — » (jamais inventée).
 * Lot 5 : `statuts` = la liste EFFECTIVE du réglage commun (libellés renommés, ajouts de la RH,
 * retirés compris pour afficher les lignes qui les portent) ; sans elle, les 7 de départ.
 */
export function statutRh(cle, statuts) {
  const depart = STATUTS_RH.find((s) => s.cle === cle);
  if (!cle) return STATUT_AUCUN;
  if (Array.isArray(statuts)) {
    const e = statuts.find((s) => s && s.cle === cle);
    if (e) {
      return {
        cle: e.cle,
        libelle: e.libelle || (depart ? depart.libelle : e.cle),
        variable: e.variable || (depart ? depart.variable : variableAjout(e.cle)),
        couleur: e.couleur || '',
        retire: !!e.retire,
        ajout: !depart,
      };
    }
  }
  return depart || STATUT_AUCUN;
}

/**
 * `var(--rh-…)` pour un statut (couleur de la bande de 5 px, de la pastille et des initiales).
 * Un statut ajouté par la RH lit --rh-statut-<clé> (posée par le fournisseur des réglages),
 * avec sa couleur en repli ; inconnu ou hors liste : --rh-aucun.
 */
export function couleurStatut(cle, statuts) {
  const st = statutRh(cle, statuts);
  if (st.ajout && st.couleur) return `var(${st.variable}, ${st.couleur})`;
  return `var(${st.variable})`;
}

// ── Renouvellement (D-10 A : seul « Retour signé » vaut accord) ───────────────────
export const RENOUVELLEMENTS = [
  { cle: 'a_faire', libelle: 'À faire', ton: 'todo' },
  { cle: 'fait', libelle: 'Fait', ton: 'ok' },
  { cle: 'retour_signe', libelle: 'Retour signé', ton: 'sig' },
  { cle: 'ko', libelle: 'KO', ton: 'ko' },
  { cle: 'sans_objet', libelle: '—', ton: '' },
];
export function renouvellement(cle) {
  return RENOUVELLEMENTS.find((r) => r.cle === cle) || RENOUVELLEMENTS[RENOUVELLEMENTS.length - 1];
}

export const EMPLOIS = ['ADS', 'CYNO', 'AUTRE'];

// Listes de choix proposées : valeurs de REPLI du lot 5 (le réglage commun du serveur les
// renomme et les complète ; lib/reglagesRh.js fusionne). Texte libre toujours accepté.
export const DECISIONS_PROPOSEES = [
  'on garde', 'on sort', 'on renouvelle', 'à voir', 'nouveau', 'passage temps plein', 'abandon de poste',
];
export const MOTIFS_PROPOSES = [
  'Fin essai', 'Fin CDD', 'Démission', 'Abandon de poste', 'Rupture conventionnelle', 'Licenciement', 'Embauche annulée',
];

// ── Dates ──────────────────────────────────────────────────────────────────────────
const RE_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/** « JJ/MM/AAAA » ou « — ». */
export function frDate(iso) {
  const m = RE_DATE.exec(String(iso || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

const enUtc = (iso) => {
  const m = RE_DATE.exec(String(iso || ''));
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null;
};

/** Nombre de jours de `a` à `b` (dates AAAA-MM-JJ) ; null si l'une manque. */
export function joursEntre(a, b) {
  const x = enUtc(a);
  const y = enUtc(b);
  if (x === null || y === null) return null;
  return Math.round((y - x) / 86400000);
}

const versIso = (ms) => new Date(ms).toISOString().slice(0, 10);

export function ajouterJours(iso, n) {
  const x = enUtc(iso);
  return x === null ? '' : versIso(x + n * 86400000);
}

/** + n mois calendaires (le 31 retombe sur le dernier jour du mois, comme relativedelta). */
export function ajouterMois(iso, n) {
  const m = RE_DATE.exec(String(iso || ''));
  if (!m) return '';
  const an = +m[1];
  const mois = +m[2] - 1 + n;
  const cible = new Date(Date.UTC(an, mois, 1));
  const dernier = new Date(Date.UTC(cible.getUTCFullYear(), cible.getUTCMonth() + 1, 0)).getUTCDate();
  return versIso(Date.UTC(cible.getUTCFullYear(), cible.getUTCMonth(), Math.min(+m[3], dernier)));
}

/** Aujourd'hui en heure de Paris (AAAA-MM-JJ). */
export function aujourdhuiParis(maintenant = new Date()) {
  try {
    return maintenant.toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });
  } catch {
    return maintenant.toISOString().slice(0, 10);
  }
}

/** Les 3 mois par défaut des heures : M−2, M−1, M (AAAA-MM). */
export function moisParDefaut(aujourdhui = aujourdhuiParis()) {
  const base = `${String(aujourdhui).slice(0, 7)}-01`;
  return [-2, -1, 0].map((n) => ajouterMois(base, n).slice(0, 7));
}

const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
/** « 2026-08 » → « août ». */
export const moisCourt = (m) => MOIS_COURTS[(+String(m || '').slice(5, 7)) - 1] || String(m || '');

// ── Prévenance (R-31) : repli d'écran, le serveur fait foi ─────────────────────────
/** Date de remise + palier du barème (24 h → +1 j ; 48 h → +2 j ; 2 semaines → +14 j ; 1 mois → +1 mois). */
export function dateApresPrevenance(remise, prevenance) {
  if (!remise || !prevenance) return '';
  const lib = sansAccents(prevenance.libelle || '');
  if (lib.includes('mois')) return ajouterMois(remise, 1);
  if (lib.includes('semaine')) return ajouterJours(remise, 14);
  if (lib.includes('48')) return ajouterJours(remise, 2);
  if (lib.includes('24')) return ajouterJours(remise, 1);
  if (Number.isFinite(prevenance.jours)) return ajouterJours(remise, prevenance.jours);
  return '';
}

/** Bornes de la fenêtre « Rompre » : minimum légal, proposition D-08, maximum = fin de période. */
export function bornesRupture({ remise, prevenance, retenueJours = null, finPeriode }) {
  const min = dateApresPrevenance(remise, prevenance);
  const avecRetenue = Number.isFinite(retenueJours) && retenueJours > 0 ? ajouterJours(remise, retenueJours) : min;
  const propose = min && avecRetenue && avecRetenue > min ? avecRetenue : min;
  if (!finPeriode) return { min, max: '', proposee: propose, plafonnee: false };
  const plafonnee = !!propose && propose > finPeriode;
  return { min: plafonnee ? finPeriode : min, max: finPeriode, proposee: plafonnee ? finPeriode : propose, plafonnee };
}

// ── Durées ─────────────────────────────────────────────────────────────────────────
const UNITES = { mois: ['mois', 'mois'], semaines: ['semaine', 'semaines'], jours: ['jour', 'jours'] };
export function libelleDuree(d) {
  if (!d || !Number.isFinite(+d.valeur) || !UNITES[d.unite]) return '—';
  const v = +d.valeur;
  const [un, plusieurs] = UNITES[d.unite];
  return `${v} ${v > 1 ? plusieurs : un}`;
}

export const LIBELLES_REGLE = {
  cdi_agent: 'CDI : 2 mois + 2 mois',
  responsable: 'Responsable (modèle du dossier DIRECTION) : 3 mois + 3 mois',
  cdd_saisie: 'CDD : durée saisie par la RH selon le contrat',
  autre: 'Durée saisie par la RH',
};
export const LIBELLES_SOURCE_DUREE = {
  regle_cdi_agent: 'règle CDI',
  regle_responsable: 'règle responsable',
  contrat: 'écrite au contrat',
  saisie: 'saisie RH',
};

// ── Initiales et nom ───────────────────────────────────────────────────────────────
export function initiales(nom) {
  const mots = String(nom || '').split(/[\s-]+/).filter(Boolean);
  if (!mots.length) return '?';
  return mots.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

// ── Frise de période d'essai (R-14) ────────────────────────────────────────────────
const borne = (x) => Math.max(0, Math.min(100, x));

/**
 * Positions en % de (date − début) / (fin PE 2 − début). Rend null si la frise manque
 * de son début ou de sa fin de PE 1 (rien n'est dessiné plutôt qu'une barre fausse).
 */
export function positionsFrise(frise) {
  if (!frise || !frise.debut || !frise.fin_pe1) return null;
  const fin = frise.fin_pe2 || frise.fin_pe1;
  const total = Math.max(1, joursEntre(frise.debut, fin) || 0);
  const pct = (d) => (d ? borne(((joursEntre(frise.debut, d) || 0) / total) * 100) : null);
  const report = Number.isFinite(+frise.report_jours) ? +frise.report_jours : 0;
  const avant = frise.fin_pe1_avant_report && report > 0 ? pct(frise.fin_pe1_avant_report) : null;
  const finPe1 = pct(frise.fin_pe1);
  return {
    aujourdhui: pct(frise.aujourdhui) ?? 0,
    finPe1,
    finPe1AvantReport: avant,
    largeurReport: avant !== null ? Math.max(0, finPe1 - avant) : 0,
    rouge: !!frise.rouge,
    reportJours: report,
    // Reprise du 26/10/2026 (trouvaille 9) : un report de 2e période se dit « PE2 reportée »
    // (report_sur rendu par le serveur ; à défaut, un report sans ancienne fin de PE 1 est
    // un report de PE 2).
    mention: report > 0
      ? `${(frise.report_sur === 'pe2' || (frise.report_sur !== 'pe1' && !frise.fin_pe1_avant_report)) ? 'PE2' : 'PE1'} reportée +${report} j`
      : 'PE1',
  };
}

// ── Filtres de l'écran (R-22, R-23) ────────────────────────────────────────────────
/**
 * Garde les lignes dont le statut RH est dans `statuts` (Set des clés actives) et dont
 * le nom contient la recherche (accents et casse ignorés). Une ligne SANS statut reste
 * toujours visible (R-23 : elle n'a pas de pastille à décocher).
 */
/**
 * Reprise du 27/10/2026 — ce qu'une ligne AFFICHE pour son statut RH. La règle « hors
 * liste » est décidée par le SERVEUR avec le réglage effectif (rh.hors_liste.statut_rh) ;
 * l'écran ne la recalcule pas. Quand le texte tapé égale aujourd'hui un libellé (ajouté
 * depuis « Valeurs hors liste », ancien libellé, valeur retirée), le serveur rend la clé à
 * afficher (rh.statut_rh_affiche) : la ligne prend le libellé et la couleur de ce statut
 * sans être réécrite. rh.statut_rh_libelle = libellé rendu par le serveur, repli quand le
 * réglage n'a pas pu être lu à l'écran.
 * Repli (serveur d'avant la reprise, sans drapeau) : clé vide + texte libre = hors liste.
 * Rend { horsListe, cle, libre, libelleServeur }.
 */
export function statutAfficheDeLaLigne(rh) {
  const r = rh || {};
  const libre = String(r.statut_rh_libre || '');
  const drapeaux = r.hors_liste && typeof r.hors_liste === 'object' ? r.hors_liste : null;
  const aDrapeau = !!drapeaux && Object.prototype.hasOwnProperty.call(drapeaux, 'statut_rh');
  const cle = r.statut_rh || r.statut_rh_affiche || '';
  let horsListe = aDrapeau ? !!drapeaux.statut_rh : (!r.statut_rh && !!libre);
  if (!horsListe && !cle && libre) horsListe = true;
  if (horsListe) return { horsListe: true, cle: '', libre: libre || String(r.statut_rh || ''), libelleServeur: '' };
  return { horsListe: false, cle, libre: '', libelleServeur: String(r.statut_rh_libelle || '') };
}

/** Même règle pour l'emploi : { horsListe, cle, libre } (drapeau du serveur rh.hors_liste.emploi). */
export function emploiAfficheDeLaLigne(rh) {
  const r = rh || {};
  const libre = String(r.emploi_libre || '');
  const drapeaux = r.hors_liste && typeof r.hors_liste === 'object' ? r.hors_liste : null;
  const aDrapeau = !!drapeaux && Object.prototype.hasOwnProperty.call(drapeaux, 'emploi');
  const cle = r.emploi || r.emploi_affiche || '';
  let horsListe = aDrapeau ? !!drapeaux.emploi : (!r.emploi && !!libre);
  if (!horsListe && !cle && libre) horsListe = true;
  if (horsListe) return { horsListe: true, cle: '', libre: libre || String(r.emploi || '') };
  return { horsListe: false, cle, libre: '' };
}

/** La clé de filtre d'une ligne : son statut RH affiché, « Hors liste » si le serveur la dit hors liste, '' sinon. */
export const cleFiltreStatut = (l) => {
  const s = statutAfficheDeLaLigne(l?.rh);
  return s.horsListe ? HORS_LISTE : s.cle;
};

export function filtrerLignes(lignes, { statuts, recherche = '', pastilles } = {}) {
  const q = sansAccents(recherche).trim();
  // Lot 5 : seule une ligne dont le statut A une pastille peut être cachée par cette pastille ;
  // un statut retiré (sans pastille) ou une clé inconnue reste toujours visible.
  const avecPastille = pastilles || new Set([...CLES_STATUTS_RH, HORS_LISTE]);
  return (Array.isArray(lignes) ? lignes : []).filter((l) => {
    const st = cleFiltreStatut(l);
    if (st && statuts && avecPastille.has(st) && !statuts.has(st)) return false;
    if (!q) return true;
    return sansAccents(l?.employee_name || '').includes(q);
  });
}

/** La ligne qui porte un contrat (lien direct ?contrat_id=, qui peut viser un avenant). */
export function ligneDuContrat(lignes, contratId) {
  if (!contratId) return null;
  return (lignes || []).find((l) => l.id === contratId
    || (Array.isArray(l.contrats_ids) && l.contrats_ids.includes(contratId))) || null;
}

/** Clé stable d'une ligne (vue agent ou contrat). */
export const cleLigne = (l) => l?.ligne_cle || (l?.id ? `contrat:${l.id}` : '');

// ── Heures corrigées (D-03 A) ──────────────────────────────────────────────────────
/** Lit une saisie d'heures : vide = null (retire la correction) ; « 26,5 » = 26.5 ; bornes 0..400. */
export function lireSaisieHeures(texte) {
  const t = String(texte ?? '').trim().replace(',', '.');
  if (!t) return { ok: true, valeur: null };
  const v = Number(t);
  if (!Number.isFinite(v)) return { ok: false, erreur: 'Nombre attendu' };
  if (v < 0 || v > 400) return { ok: false, erreur: 'Entre 0 et 400 h' };
  return { ok: true, valeur: Math.round(v * 100) / 100 };
}

/** « 61 », « 26,5 » ou « — » (planning absent = null, jamais 0 inventé). */
export function libelleHeures(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return String(Math.round(n * 100) / 100).replace('.', ',');
}

// ── Lot 3 (26/10/2026) : compteurs d'alertes et onglet « À faire » ──────────────────
// Le calcul vit côté serveur (GET /api/suivi-rh/alertes, utils/alertes_suivi_rh.py) :
// l'écran place les tuiles dans l'ordre de la maquette de Mathilde et dit ce qui revient.

/** Ordre des compteurs (maquette) : les alertes calculées, « Courriers à envoyer » suivie des deux
 *  tuiles de procédure du lot 4, puis « Agents sous contrat » (chiffre). */
export const ORDRE_TUILES = [
  'pe_expiree', 'retour_signe_non_retrouve', 'fin_periode_15j',
  'courriers_a_envoyer', 'delai_mise_en_demeure', 'mise_a_pied_en_cours', 'zero_heure_deux_mois',
  'agents_sous_contrat',
];
/** Les 7 alertes calculées par le serveur (dans `data`, ordre de utils/alertes_suivi_rh.ALERTES) :
 *  les 4 du lot 3 puis les 3 des courriers RH (lot 4, 26/10/2026). */
export const CLES_ALERTES = [
  'pe_expiree', 'retour_signe_non_retrouve', 'fin_periode_15j', 'zero_heure_deux_mois',
  'courriers_a_envoyer', 'delai_mise_en_demeure', 'mise_a_pied_en_cours',
];

/** Couleur du chiffre d'une tuile, par ton rendu par le serveur. */
export const TONS_TUILE = {
  rouge: 'var(--rh-rouge)',
  or: 'var(--rh-or)',
  neutre: '#ECECF0',
};

/** Libellés de repli des codes d'action (le serveur donne le sien ; jamais un geste inventé). */
export const LIBELLES_ACTIONS = {
  decider: 'Décider',
  renouveler: 'Renouveler',
  confirmer: 'Confirmer',
  rompre: 'Rompre',
  retour_signe: 'Retour signé ?',
  relancer: 'Relancer',
  sortir: 'Sortir',
  passer_a_verifier: 'Passer à « À vérifier »',
  verifier_planning: 'Vérifier le planning',
  ouvrir_ligne: 'Ouvrir la ligne',
  // Lot 4 (26/10/2026) — gestes des courriers RH (tuiles « Courriers à envoyer »,
  // « Mise en demeure · délai à suivre », « Mise à pied conservatoire en cours »).
  generer_courrier: 'Générer le courrier',
  envoyer_mail: 'Envoyer par mail',
  marquer_envoye: 'Marquer envoyé',
  ouvrir_courrier: 'Ouvrir le courrier',
  presentation: 'Première présentation',
  reponse: 'Réponse reçue',
  lever_mise_a_pied: 'Lever la mise à pied',
};

const TUILE_COURRIERS_DEFAUT = { cle: 'courriers_a_envoyer', libelle: 'Courriers à envoyer', texte: 'livré au lot 4', nombre: null };

/**
 * Les 6 tuiles à afficher, dans l'ordre de la maquette :
 *   - les alertes de `resume.data` (une tuile de clé inconnue est gardée, à la fin des alertes) ;
 *   - « Courriers à envoyer » : depuis le lot 4 (26/10/2026), une alerte de `data` comme les
 *     autres (cliquable, lignes et porte de sortie) ; un serveur d'avant le lot 4 la rend
 *     encore dans `a_venir` : elle reste alors grisée, jamais cliquable ;
 *   - « Agents sous contrat » lue dans `agents_sous_contrat` (non cliquable).
 * Chaque tuile rendue porte `genre` : « alerte » | « a_venir » | « chiffre ».
 * Une alerte attendue mais absente de la réponse est rendue avec nombre null (« indisponible »),
 * jamais 0 : un compteur qui n'a pas été calculé ne se présente pas comme vide.
 */
export function tuilesAffichees(resume) {
  if (!resume || typeof resume !== 'object') return [];
  const data = Array.isArray(resume.data) ? resume.data : [];
  const parCle = new Map(data.filter((t) => t && t.cle).map((t) => [t.cle, t]));
  const aVenir = (Array.isArray(resume.a_venir) ? resume.a_venir : []).find((t) => t && t.cle === 'courriers_a_envoyer')
    || TUILE_COURRIERS_DEFAUT;
  const asc = resume.agents_sous_contrat || {};
  const sortie = [];
  for (const cle of ORDRE_TUILES) {
    if (cle === 'courriers_a_envoyer' && parCle.has(cle)) {
      sortie.push({ ...parCle.get(cle), genre: 'alerte' });
    } else if (cle === 'courriers_a_envoyer') {
      sortie.push({ ...aVenir, genre: 'a_venir', ton: 'neutre', lignes: [] });
    } else if (cle === 'agents_sous_contrat') {
      sortie.push({
        cle, genre: 'chiffre', ton: 'neutre', libelle: 'Agents sous contrat',
        nombre: Number.isFinite(asc.nombre) ? asc.nombre : null, source: asc.source || '', lignes: [],
      });
    } else if (parCle.has(cle)) {
      sortie.push({ ...parCle.get(cle), genre: 'alerte' });
    } else {
      sortie.push({ cle, genre: 'alerte', ton: 'neutre', libelle: cle, nombre: null, lignes: [], absente: true });
    }
    if (cle === 'fin_periode_15j') {
      // Les alertes inconnues du lot 3 (ajoutées plus tard par le serveur) restent visibles.
      for (const t of data) {
        if (t && t.cle && !ORDRE_TUILES.includes(t.cle)) sortie.push({ ...t, genre: 'alerte' });
      }
    }
  }
  return sortie;
}

/** Une tuile s'ouvre seulement si c'est une alerte calculée, au nombre > 0. */
export const tuileOuvrable = (t) => !!(t && t.genre === 'alerte' && Number.isFinite(t.nombre) && t.nombre > 0);

/** « — · indisponible » pour un nombre absent, jamais 0. */
export const libelleNombre = (n) => (Number.isFinite(n) ? String(n) : '— · indisponible');

/** Filtre par défaut de l'onglet À faire : « Fins d'essai », sinon « Tous » s'il est vide. */
export function filtreAFaireParDefaut(filtres) {
  const f = filtres || {};
  return Number(f.fin_essai) > 0 ? 'fin_essai' : 'tous';
}

/** Les cartes d'un filtre (fin_essai | renouvellement | tous). */
export function cartesDuFiltre(cartes, filtre) {
  const liste = Array.isArray(cartes) ? cartes : [];
  if (filtre === 'fin_essai' || filtre === 'renouvellement') return liste.filter((c) => c && c.type === filtre);
  return liste;
}

// ── Reprise du 26/10/2026 (lot 3) ─────────────────────────────────────────────────

/** « JJ/MM/AAAA » ou « AAAA-MM-JJ » → « AAAA-MM-JJ » ; '' si illisible. */
export function lireDateSaisie(texte) {
  const t = String(texte || '').trim();
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  const iso = m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : (RE_DATE.exec(t) ? t.slice(0, 10) : '');
  if (!iso) return '';
  m = RE_DATE.exec(iso);
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return versIso(d.getTime()) === iso ? iso : '';
}

/**
 * La date du « Retour signé ? » (trouvaille 3 de la reprise) : jusqu'à la fin de la 1re
 * période, c'est le jour même (geste du lot 1, inchangé). Après, la date de SIGNATURE de
 * l'agent est demandée, et une signature postérieure à la fin de la 1re période est dite
 * avant d'enregistrer : elle n'ouvre pas la 2e période de plein droit (à voir avec la
 * direction ; ce n'est pas un conseil d'avocat).
 * Rend { date } à enregistrer, { annule: true }, ou { erreur } (rien n'est enregistré).
 */
export function dateDuRetourSigne(finPe1, aujourdhui = aujourdhuiParis(), fenetre = window) {
  const fin = String(finPe1 || '').slice(0, 10);
  if (!RE_DATE.test(fin) || aujourdhui <= fin) return { date: aujourdhui };
  const saisie = fenetre.prompt(
    `La 1re période s’est terminée le ${frDate(fin)}. Date de signature du renouvellement par l’agent (JJ/MM/AAAA) :`,
    frDate(aujourdhui),
  );
  if (saisie === null || saisie === undefined) return { annule: true };
  const iso = lireDateSaisie(saisie);
  if (!iso) return { erreur: 'Date de signature illisible : rien n’est enregistré.' };
  if (iso > aujourdhui) return { erreur: 'Date de signature dans le futur : rien n’est enregistré.' };
  if (iso > fin && !fenetre.confirm(
    `Signé le ${frDate(iso)}, après la fin de la 1re période (${frDate(fin)}) : ce retour n’ouvre pas la 2e période de plein droit, à voir avec la direction (ce n’est pas un conseil d’avocat). Enregistrer quand même ?`,
  )) return { annule: true };
  return { date: iso };
}

/** Libellé d'une erreur du résumé dont la clé n'est pas une tuile (charte 1.3 : pas de clé brute). */
export const LIBELLES_ERREURS = {
  periode_essai: 'Alertes de période d’essai',
  a_faire: 'Onglet À faire',
};

/** Le résumé a-t-il été calculé un autre jour que celui de l'écran (heure de Paris) ? */
export const resumeDUnAutreJour = (resume, aujourdhui = aujourdhuiParis()) => !!(
  resume && resume.aujourdhui && String(resume.aujourdhui).slice(0, 10) !== aujourdhui);
